import * as React from 'react'

import { MobXProviderContext, observer } from 'mobx-react'
import { Controller } from 'react-hook-form'

import Localization from '~/client/src/shared/localization/LocalizationManager'

import CompanyListSelector from '../../CompanyListSelector/CompanyListSelector'
import { useCompanyListSelectorData } from '../../CompanyListSelector/useCompanyListSelectorData'
import FullScreenPickerSheet from '../../FullScreenPickerSheet/FullScreenPickerSheet'
import StruxhubCompanySelector from '../../StruxhubInputs/StruxhubSelector/StruxhubCompanySelector'
import { CompanyFieldConfig } from '../types'

interface CompanyFieldProps {
  field: CompanyFieldConfig
  control: any
  error?: string
  hideHelperText?: boolean
}

interface CompanyPickerBodyProps {
  label: string
  value: string | null | undefined
  onChange: (value: string | null) => void
  onClose: () => void
}

// Extracted so the heavy selector-data hook runs only when the picker opens,
// not on every form render. `observer` keeps those reads reactive.
const CompanyPickerBody = observer(function CompanyPickerBody({
  label,
  value,
  onChange,
  onClose,
}: CompanyPickerBodyProps) {
  const stores = React.useContext(MobXProviderContext) as any
  const { companies, companyMetadata, currentCompany } =
    useCompanyListSelectorData({
      companiesStore: stores.companiesStore,
      projectTradesStore: stores.projectTradesStore,
      userProjectsStore: stores.userProjectsStore,
      currentUser: stores.state?.user,
    })

  return (
    <CompanyListSelector
      title={Localization.translator.selectX(label)}
      selectedCompanyIds={new Set(value ? [value] : [])}
      onCompanyToggle={companyId =>
        onChange(companyId === value ? null : companyId)
      }
      companies={companies}
      companyMetadata={companyMetadata}
      currentCompany={currentCompany}
      selectionMode="single"
      onClose={onClose}
      className="tw:flex-1"
    />
  )
})

export default function CompanyField({
  field,
  control,
  error,
  hideHelperText,
}: CompanyFieldProps) {
  const [isPickerOpen, setIsPickerOpen] = React.useState(false)

  const closePicker = () => setIsPickerOpen(false)

  return (
    <Controller
      name={field.name}
      control={control}
      defaultValue={field.defaultValue}
      rules={{
        required: field.required
          ? `${field.label || field.name} is required`
          : false,
      }}
      render={({ field: { onChange, value } }) => (
        <>
          <StruxhubCompanySelector
            label={field.label}
            value={value}
            isRequired={field.required}
            isReadOnly={field.readOnly || field.displayOnly}
            isDisabled={field.disabled}
            isValid={!error}
            validationMessage={error}
            isHelperTextHidden={hideHelperText}
            onClick={() => setIsPickerOpen(true)}
            onValueReset={() => onChange(undefined)}
          />
          <FullScreenPickerSheet isOpen={isPickerOpen} onClose={closePicker}>
            <CompanyPickerBody
              label={field.label || field.name}
              value={value}
              onChange={onChange}
              onClose={closePicker}
            />
          </FullScreenPickerSheet>
        </>
      )}
    />
  )
}
